import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TrendingUp, Wallet, FileText, AlertTriangle, Plus } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { api } from "@/lib/api";
import { money, fmtDate } from "@/lib/format";
import { useApp } from "@/context/AppCtx";
import { PageHeader, Kpi, StatusBadge, Empty } from "@/components/kit";
import { Button } from "@/components/ui/button";

export const ChartTip = ({ active, payload, label }) =>
  active && payload?.length ? (
    <div className="panel px-3 py-2 text-xs">
      <div className="text-slate-400">{label}</div>
      <div className="mono text-[#00F0FF] font-semibold">{money(payload[0].value)}</div>
    </div>
  ) : null;

export default function Dashboard() {
  const { base } = useApp();
  const [d, setD] = useState(null);

  useEffect(() => {
    const load = () => api.get("/dashboard").then((r) => setD(r.data));
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  if (!d) return <div className="text-slate-400">Cargando…</div>;

  return (
    <div>
      <PageHeader title="Panel general" subtitle="Resumen en tiempo real de ventas, cobros e inventario">
        <Button asChild variant="outline" data-testid="dash-new-quote">
          <Link to={`${base}/proformas/nueva`}><Plus className="w-4 h-4 mr-1" />Proforma</Link>
        </Button>
        <Button asChild className="btn-neon" data-testid="dash-new-invoice">
          <Link to={`${base}/facturas/nueva`}><Plus className="w-4 h-4 mr-1" />Factura</Link>
        </Button>
      </PageHeader>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <Kpi testId="kpi-month-sales" label="Ventas del mes" value={money(d.month_sales)} icon={TrendingUp} hint={`${d.month_invoices} facturas`} />
        <Kpi testId="kpi-receivables" label="Por cobrar" value={money(d.receivables)} icon={Wallet} accent="#F59E0B" hint={`${d.pending_count} pendientes`} />
        <Kpi testId="kpi-quotes" label="Proformas vigentes" value={d.active_quotes} icon={FileText} accent="#9D4EDD" />
        <Kpi testId="kpi-low-stock" label="Stock bajo" value={d.low_stock.length} icon={AlertTriangle} accent="#F43F5E" hint={`Inventario: ${money(d.inventory_value)}`} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mt-6">
        <div className="panel p-5 xl:col-span-2" data-testid="dash-sales-chart">
          <div className="label-cap mb-4">Ventas últimos 30 días</div>
          <div className="h-64">
            {d.by_day.length ? (
              <ResponsiveContainer>
                <AreaChart data={d.by_day.map((x) => ({ ...x, date: fmtDate(x.date).slice(0, 5) }))}>
                  <defs>
                    <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#00F0FF" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#9D4EDD" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#271E56" strokeDasharray="3 3" />
                  <XAxis dataKey="date" stroke="#64748B" fontSize={11} />
                  <YAxis stroke="#64748B" fontSize={11} width={70} tickFormatter={(v) => `₡${(v / 1000).toFixed(0)}k`} />
                  <Tooltip content={<ChartTip />} />
                  <Area type="monotone" dataKey="total" stroke="#00F0FF" strokeWidth={2} fill="url(#g1)" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <Empty text="Aún no hay ventas en este periodo" />
            )}
          </div>
        </div>

        <div className="panel p-5" data-testid="dash-low-stock">
          <div className="label-cap mb-4">Alertas de stock bajo</div>
          {d.low_stock.length ? (
            <ul className="space-y-2">
              {d.low_stock.map((p) => (
                <li key={p.id} className="flex items-center justify-between rounded-xl bg-amber-500/5 border border-amber-500/20 px-3 py-2">
                  <div className="min-w-0">
                    <div className="text-sm text-white truncate">{p.name}</div>
                    <div className="text-xs mono text-slate-400">{p.code}</div>
                  </div>
                  <span className="mono text-amber-300 text-sm">{p.stock} / {p.min_stock}</span>
                </li>
              ))}
            </ul>
          ) : (
            <Empty text="Todo el inventario está en niveles normales" />
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mt-6">
        <div className="panel p-5 xl:col-span-2 overflow-x-auto" data-testid="dash-recent-invoices">
          <div className="label-cap mb-4">Últimas facturas</div>
          <table className="w-full text-sm min-w-[520px]">
            <tbody>
              {d.recent_invoices.map((i) => (
                <tr key={i.id} className="border-t border-[#271E56]">
                  <td className="py-2.5"><Link className="mono text-[#00F0FF] hover:underline" to={`${base}/facturas/${i.id}`}>{i.number}</Link></td>
                  <td className="text-slate-300">{i.client?.name || "Cliente contado"}</td>
                  <td className="text-slate-400">{fmtDate(i.issue_date)}</td>
                  <td className="mono text-right text-white">{money(i.total, i.currency)}</td>
                  <td className="text-right pl-3"><StatusBadge status={i.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          {!d.recent_invoices.length && <Empty text="Sin facturas todavía" />}
        </div>
        <div className="panel p-5" data-testid="dash-top-products">
          <div className="label-cap mb-4">Más vendidos (30 días)</div>
          <ul className="space-y-3">
            {d.top_products.map((p, i) => (
              <li key={p.name} className="flex items-center gap-3">
                <span className="w-7 h-7 rounded-lg bg-[#1A143D] flex items-center justify-center mono text-xs text-[#9D4EDD]">{i + 1}</span>
                <span className="flex-1 text-sm truncate">{p.name}</span>
                <span className="mono text-xs text-slate-400">{p.qty} u.</span>
              </li>
            ))}
          </ul>
          {!d.top_products.length && <Empty text="Sin datos" />}
        </div>
      </div>
    </div>
  );
}
